import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChefHat, Zap, Truck, Star, ArrowRight, Sparkles, Clock, Heart } from 'lucide-react';
import './Home.css';

const Home: React.FC = () => {
  const { currentUser } = useAuth();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const stats = [
    { number: "1000+", label: "Сэтгэл хангалуун үйлчлүүлэгч", icon: <Heart size={24} /> },
    { number: "15 мин", label: "Дундаж хүргэлтийн хугацаа", icon: <Clock size={24} /> },
    { number: "50+", label: "Янз бүрийн орц", icon: <Sparkles size={24} /> },
    { number: "24/7", label: "Үйлчилгээний цаг", icon: <Star size={24} /> }
  ];

  return (
    <div className="home">
      <section className={`hero ${isVisible ? 'fade-in' : ''}`}>
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles size={16} />
            <span>Шинэ AI технологи</span>
          </div>
          
          <h1 className="hero-title">
            <span className="title-line">Монголын</span>
            <span className="title-line title-highlight">Хамгийн амттай</span>
            <span className="title-line">Бургерыг захиална уу!</span>
          </h1>
          
          <p className="hero-subtitle">
            Та өөрийн хүссэн орцтойгоор бургер бүтээж, шуурхай захиалга өгөх боломжтой.
            Манай шинэ AI технологийн тусламжтайгаар таны төгс бургерыг бүтээнэ.
          </p>
          
          <div className="hero-actions">
            {currentUser ? (
              <Link to="/burger-builder" className="btn btn-primary btn-hero">
                <ChefHat size={20} />
                <span>Бургер бүтээх</span>
                <ArrowRight size={20} />
              </Link>
            ) : (
              <>
                <Link to="/login" className="btn btn-primary btn-hero">
                  <span>Нэвтрэх</span>
                  <ArrowRight size={20} />
                </Link>
                <Link to="/login" className="btn btn-secondary btn-hero">
                  <span>Шинээр бүртгүүлэх</span>
                </Link>
              </>
            )}
          </div>
          
          <div className="hero-stats">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-content">
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="hero-visual">
          <div className="burger-showcase">
            <div className="burger-card">
              <div className="burger-emoji">🍔</div>
              <div className="burger-glow"></div>
            </div>
            <div className="floating-ingredients">
              <div className="ingredient ingredient-1">🥬</div>
              <div className="ingredient ingredient-2">�</div>
              <div className="ingredient ingredient-3">🧀</div>
              <div className="ingredient ingredient-4">🥓</div>
            </div>
          </div>
        </div>
      </section>

      {!currentUser && (
        <section className="auth-section">
          <div className="auth-content">
            <div className="auth-header">
              <h2 className="auth-title">Эхлэхийн тулд нэвтэрнэ үү</h2>
              <p className="auth-subtitle">
                Өөрийн дуртай бургер бүтээж, захиалга өгөхийн тулд нэвтэрнэ үү
              </p>
            </div>
            
            <div className="auth-cards">
              <div className="auth-card">
                <div className="auth-card-icon">
                  <span>👤</span>
                </div>
                <h3>Нэвтрэх</h3>
                <p>Өмнө нь бүртгүүлсэн бол энд дарж нэвтэрнэ үү</p>
                <Link to="/login" className="btn btn-primary btn-auth">
                  Нэвтрэх
                </Link>
              </div>
              
              <div className="auth-card">
                <div className="auth-card-icon">
                  <span>✨</span>
                </div>
                <h3>Шинээр бүртгүүлэх</h3>
                <p>Анх удаа ирж байгаа бол энд дарж бүртгүүлнэ үү</p>
                <Link to="/login?register=true" className="btn btn-secondary btn-auth">
                  Бүртгүүлэх
                </Link>
              </div>
            </div>
            
            <div className="auth-demo">
              <p className="demo-text">
                <span className="demo-icon">🎮</span>
                Demo режимд туршиж үзэх: admin@gmail.com / 123456
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="features">
        <div className="section-header">
          <h2 className="section-title">Яагаад Burger AI-г сонгох вэ?</h2>
          <p className="section-subtitle">Технологи, чанар, хурд - бүгдийг нэгтгэсэн цогц шийдэл</p>
        </div>
        
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-header">
              <div className="feature-icon">
                <ChefHat size={32} />
              </div>
              <h3>Өөрийн бургер бүтээх</h3>
            </div>
            <p>AI-н тусламжтайгаар олон төрлийн орцуудаас сонгож өөрийн төгс бургерыг бүтээнэ үү</p>
            <div className="feature-benefits">
              <span className="benefit">50+ орц</span>
              <span className="benefit">AI зөвлөмж</span>
              <span className="benefit">Шилжих орц</span>
            </div>
          </div>
          
          <div className="feature-card">
            <div className="feature-header">
              <div className="feature-icon">
                <Zap size={32} />
              </div>
              <h3>Хурдан захиалга</h3>
            </div>
            <p>Хэдхэн товчлуураар л хурдан бөгөөд хялбар захиалга өгөөд бэлэн болтлоо хүлээнэ үү</p>
            <div className="feature-benefits">
              <span className="benefit">1-Click захиалга</span>
              <span className="benefit">Түргэн төлбөр</span>
              <span className="benefit">Онлайн хяналт</span>
            </div>
          </div>
          
          <div className="feature-card">
            <div className="feature-header">
              <div className="feature-icon">
                <Truck size={32} />
              </div>
              <h3>Хурдан хүргэлт</h3>
            </div>
            <p>Дундажаар 15 минутын дотор таны захиалгыг хамгийн халуухан хүргэж өгнө</p>
            <div className="feature-benefits">
              <span className="benefit">15 мин хүргэлт</span>
              <span className="benefit">GPS хяналт</span>
              <span className="benefit">Халуун бургер</span>
            </div>
          </div>
          
          <div className="feature-card">
            <div className="feature-header">
              <div className="feature-icon">
                <Star size={32} />
              </div>
              <h3>Чанартай орц</h3>
            </div>
            <p>Зөвхөн шинэ бөгөөд дээд зэргийн чанартай орцуудыг ашиглан амттай бургер бэлтгэнэ</p>
            <div className="feature-benefits">
              <span className="benefit">Органик орц</span>
              <span className="benefit">Өдөр тутмын шинэчлэл</span>
              <span className="benefit">Чанарын батламж</span>
            </div>
          </div>
        </div>
      </section>

      {currentUser && (
        <section className="user-section">
          <div className="user-welcome">
            <div className="welcome-content">
              <h2 className="welcome-title">Сайн байна уу, {currentUser.email?.split('@')[0] || 'Хэрэглэгч'}!</h2>
              <p className="welcome-subtitle">Таны дараагийн амттай бургерыг бэлдэж байна</p>
            </div>
            <div className="welcome-avatar">
              <div className="avatar-circle">
                <span>{currentUser.email?.[0]?.toUpperCase() || 'Х'}</span>
              </div>
            </div>
          </div>
          
          <div className="user-actions">
            <Link to="/orders" className="btn btn-primary btn-action">
              <span>Миний захиалгууд</span>
              <ArrowRight size={18} />
            </Link>
            <Link to="/burger-builder" className="btn btn-secondary btn-action">
              <ChefHat size={18} />
              <span>Шинэ бургер захиалах</span>
            </Link>
          </div>
        </section>
      )}

      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Өнөөдөр л эхэлцгээе!</h2>
          <p className="cta-subtitle">Таны амттай бургерийн аялал энд эхэлнэ</p>
          <Link to="/burger-builder" className="btn btn-primary btn-cta">
            <Sparkles size={20} />
            <span>Одоо захиалах</span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
